import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import Logo from '../../../../assets/images/logo.png';
import { Link } from 'react-router-dom';
import { googleLogout } from '@react-oauth/google';
import Login from '../../../../components/Login'
import { useNavigate, useLocation } from "react-router-dom";
import { revokeGoogleToken, backendApi } from '../../../../components/Api';

// Log the user out after this long without mouse/keyboard activity.
const INACTIVITY_LIMIT_MINUTES = 30;

const Header = ({ onSidebarOpen, userDetailsdata, colorInvert = false }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { mode } = theme.palette;

  const location = useLocation();
  const serverUrl = process.env.REACT_APP_SERVER_URL

  const userDetails = userDetailsdata || '';

  const expireAt = location?.state?.expire_at ? location?.state?.expire_at : localStorage.getItem('expire_at')
  const accessToken = location?.state?.accessToken ? location?.state?.accessToken : localStorage.getItem('access_token')

  const logout = useCallback(async () => {
    if (userDetails?.email) {
      await backendApi(`${serverUrl}api/auth/logout`, { email: userDetails.email })
    }
    if (accessToken) {
      await revokeGoogleToken(accessToken)
    }
    googleLogout()
    localStorage.clear()
    navigate('/', { state: {} })
  }, [accessToken, navigate, serverUrl, userDetails]);

  // Track the last interaction so an idle session can be torn down.
  useEffect(() => {
    const touch = () => localStorage.setItem('lastActvity', new Date());
    document.addEventListener('mousemove', touch);
    document.addEventListener('click', touch);
    return () => {
      document.removeEventListener('mousemove', touch);
      document.removeEventListener('click', touch);
    };
  }, []);

  useEffect(() => {
    if (!userDetails) {
      return undefined;
    }
    const timeInterval = setInterval(() => {
      const lastActivity = localStorage.getItem('lastActvity')
      if (!lastActivity) {
        return;
      }
      const diffMs = Math.abs(new Date(lastActivity) - new Date());
      const minutes = Math.floor(diffMs / 1000 / 60);
      if (minutes >= INACTIVITY_LIMIT_MINUTES) {
        clearInterval(timeInterval);
        logout();
      }
    }, 1000)
    return () => clearInterval(timeInterval);
  }, [logout, userDetails]);

  // Drop an expired session. Only ever runs for a user we still think is signed
  // in, so a stale `expire_at` left in localStorage cannot wedge the landing page.
  useEffect(() => {
    if (!userDetails || !expireAt) {
      return;
    }
    if (Math.round(new Date().getTime() / 1000) > Number(expireAt)) {
      logout();
    }
  }, [expireAt, logout, userDetails]);

  return (
    <>
      <Box className="header-box"
        display={'flex'}
        justifyContent={'space-between'}
        alignItems={'center'}
        width={1}
        height={{ xs: 60, md: 80 }}
        paddingY={1}
        boxShadow={'none'}
      >
        <Link component="a"
          to="/"

          className="main-logo"
        >
          <Box
            component={'img'}
            src={Logo}
            width={{ xs: 180, sm: 200, md: 250 }}
          />
        </Link>
        <Box className="main-menu" sx={{ display: { xs: 'none', md: 'flex' } }} alignItems={'center'}>
          {window.location.pathname === '/analytics-report' ?
            <Box className="menu-item">
              <Link
                component="a"
                to="/"
              >Take Another Test
              </Link>
            </Box> : ''}
          <Box className="menu-item">
            <Link
              component="a"
              to="/contact"
            >Contact us
            </Link>
          </Box>
          {!userDetails ?
            <Box className="header-btn" marginLeft={2}>
              <Login endIcon={false}/>
            </Box> :
            <Box marginLeft={2}>
              <Button id="logoutButton" className='theme-btn lite-btn' onClick={logout} size="small">Log out</Button>
            </Box>}
        </Box>
        <Box className="menuToggle" sx={{ display: { xs: 'block', md: 'none' } }} alignItems={'center'}>
          <Button
            onClick={() => onSidebarOpen()}
            aria-label="Menu"
            variant={'outlined'}
            sx={{
              borderRadius: 2,
              minWidth: 'auto',
              padding: 1,
            }}
          >
            <MenuIcon />
          </Button>
        </Box>
      </Box>
    </>
  );
};

Header.propTypes = {
  onSidebarOpen: PropTypes.func,
  colorInvert: PropTypes.bool,
};

export default Header;
